import { ErrorModal } from "@dexteel/mesf-core";
import {
	Box,
	CircularProgress,
	GlobalStyles,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import type {
	ColDef,
	GridApi,
	GridReadyEvent,
	ICellRendererParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import moment, { type Moment } from "moment";
import { useCallback, useMemo, useRef, useState } from "react";
import {
	isUsableDate,
	urlParam,
	useDropUnknownValue,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import {
	useStraightenerKpisData,
	useStraightenerKpisPickers,
} from "../hooks/useStraightenerKpis";
import {
	ALL_PRODUCT_SIZE_ID,
	ALL_PRODUCT_SIZE_OPTION,
	CREW_OPTIONS,
	NO_PRODUCT_SIZE_OPTION,
	SHIFT_OPTIONS,
	type StraightenerKpiRow,
} from "../models/StraightenerKpis";
import { StraightenerKpisToolbar } from "./StraightenerKpisToolbar";

const CENTER_CELL = { textAlign: "center" } as const;
const CENTER_HEADER = "ag-cell-center-header";
const HEADER_HEIGHT = 32;
const ROW_HEIGHT = 28;
const NO_ROWS_OVERLAY = `<span style="color:${mesfColors.text.secondary};font-size:13px">No straightener runs were recorded in the selected range</span>`;

const DECIMAL_COLUMNS = new Set([
	"GapTimeMin",
	"GapTimeMax",
	"GapTimeAvg",
	"FPMSet",
	"FPMCalibrated",
	"EstimatedDurationAvgSeconds",
	"BarLengthAvgFeet",
]);

// Production dates are calendar labels, not timestamps to convert.
const asDate = (value: string | null | undefined): string =>
	value ? String(value).slice(0, 10) : "";

const asInteger = (value: number | null | undefined): string =>
	value == null ? "" : String(value);

// Two decimals and no thousands separator, which is what the legacy grid's
// toFixed(2) produces. That grid hides a real zero behind an empty cell; this
// one shows it.
const asNumber = (value: number | null | undefined): string =>
	value == null ? "" : value.toFixed(2);

const productDisplay = (row?: StraightenerKpiRow): string =>
	row?.ProductSize?.trim() || row?.PlcProductDescription || "";

const layerDisplay = (row?: StraightenerKpiRow): string => {
	if (row?.TargetBarsPerLayerMin == null) return "";
	return row.TargetBarsPerLayerMax == null ||
		row.TargetBarsPerLayerMin === row.TargetBarsPerLayerMax
		? String(row.TargetBarsPerLayerMin)
		: `${row.TargetBarsPerLayerMin}–${row.TargetBarsPerLayerMax}`;
};

const ProductCell = ({ data }: ICellRendererParams<StraightenerKpiRow>) => {
	if (!data) return null;
	const issue = data.ProductMappingIssue;
	return (
		<Tooltip
			arrow
			describeChild
			title={
				<Box sx={{ whiteSpace: "pre-line" }}>
					{issue ? `${issue}\n` : ""}
					{data.PlcProductDescription
						? `Recorded PLC: ${data.PlcProductDescription}\n`
						: ""}
					{`MES product ID: ${data.ProductSizeId ?? "unmatched"}`}
				</Box>
			}
		>
			<Box
				component="span"
				tabIndex={issue ? 0 : undefined}
				sx={{
					display: "inline-flex",
					alignItems: "center",
					gap: 0.5,
					maxWidth: "100%",
					color: issue ? "error.main" : "#000",
				}}
			>
				{issue && (
					<WarningAmberIcon
						fontSize="small"
						titleAccess="Unresolved MES product"
						sx={{ flexShrink: 0 }}
					/>
				)}
				<Box
					component="span"
					sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
				>
					{productDisplay(data)}
				</Box>
			</Box>
		</Tooltip>
	);
};

// What each column actually is, in one line. Shown on the header and on every
// cell as a native title, so hovering answers "what is this number" without
// leaving the grid. Same pattern as the Product Changes report.
const DESCRIPTIONS: Record<string, string> = {
	ProductionDate:
		"Production date of the shift. The selected range filters run timestamps in plant local time; night shifts can belong to the previous production date.",
	ProductionShift: "D for the day shift, N for the night shift",
	CrewId: "Crew working that shift. Empty when none was on the roster",
	ProductSize:
		"Full MES product name, or the recorded PLC description in red when matching failed",
	Count:
		"Selected straightener passes in this group, not the number of individual bars",
	GapTimeMin: "Minimum recorded PLC gap among these runs, in seconds",
	GapTimeMax: "Maximum recorded PLC gap among these runs, in seconds",
	GapTimeAvg:
		"Average recorded PLC gap among these runs, excluding missing readings",
	FPMSet: "Line speed the straightener was set to, in feet per minute",
	FPMCalibrated:
		"Average calibrated PLC speed for these runs, in feet per minute; this is not an instantaneous RUN signal",
	EstimatedDurationAvgSeconds:
		"Average estimated processing duration of runs with duration evidence, in seconds",
	TargetBarsPerLayerMin:
		"Configured bars per layer. A range means the target changed within this group; it is not a measured bar count",
	BarLengthAvgFeet:
		"Average configured bar length of runs with length data, in feet",
};

// Column order follows the legacy grid, which is not the order the procedure
// returns: the gap times come before the two speeds there.
const RAW_COLUMN_DEFS: ColDef<StraightenerKpiRow>[] = [
	{
		field: "ProductionDate",
		headerName: "Prod Date",
		flex: 12,
		minWidth: 110,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asDate(p.value),
	},
	{
		field: "ProductionShift",
		headerName: "Shift",
		flex: 7,
		minWidth: 70,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "CrewId",
		headerName: "Crew",
		flex: 7,
		minWidth: 70,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
	},
	{
		field: "ProductSize",
		headerName: "Product",
		flex: 18,
		minWidth: 220,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueGetter: (p) => productDisplay(p.data),
		cellRenderer: ProductCell,
	},
	{
		field: "Count",
		headerName: "Runs",
		flex: 9,
		minWidth: 85,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asInteger(p.value),
	},
	{
		field: "GapTimeMin",
		headerName: "Gap Time Min",
		flex: 12,
		minWidth: 115,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asNumber(p.value),
	},
	{
		field: "GapTimeMax",
		headerName: "Gap Time Max",
		flex: 12,
		minWidth: 115,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asNumber(p.value),
	},
	{
		field: "GapTimeAvg",
		headerName: "Gap Time Avg",
		flex: 12,
		minWidth: 115,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asNumber(p.value),
	},
	{
		field: "FPMSet",
		headerName: "FPM Set",
		flex: 10,
		minWidth: 95,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asNumber(p.value),
	},
	{
		field: "FPMCalibrated",
		headerName: "FPM Calibrated",
		flex: 13,
		minWidth: 125,
		cellStyle: CENTER_CELL,
		headerClass: CENTER_HEADER,
		valueFormatter: (p) => asNumber(p.value),
	},
];

const RUN_COLUMNS: ColDef<StraightenerKpiRow>[] = [
	{
		field: "EstimatedDurationAvgSeconds",
		headerName: "Avg. est. duration (s)",
		minWidth: 165,
		valueFormatter: (p) => asNumber(p.value),
	},
	{
		field: "TargetBarsPerLayerMin",
		headerName: "Bars/layer (set)",
		minWidth: 135,
		valueFormatter: (p) => layerDisplay(p.data),
	},
	{
		field: "BarLengthAvgFeet",
		headerName: "Avg. bar length (ft)",
		minWidth: 155,
		valueFormatter: (p) => asNumber(p.value),
	},
];

const withTooltips = (
	defs: ColDef<StraightenerKpiRow>[],
): ColDef<StraightenerKpiRow>[] =>
	defs.map((def) => {
		const text = def.field ? DESCRIPTIONS[def.field] : undefined;
		if (!text) return def;
		if (def.field === "ProductSize") return { ...def, headerTooltip: text };
		return { ...def, headerTooltip: text, tooltipValueGetter: () => text };
	});

const COLUMN_DEFS = withTooltips([...RAW_COLUMN_DEFS, ...RUN_COLUMNS]);

// The legacy screen opens on the last three days: today minus two, to today.
const defaultStart = () => moment().startOf("day").subtract(2, "day");
const defaultEnd = () => moment().startOf("day");

// The product size carries two sentinels: -2 is the "All" option, which the procedure
// gets as null and never reaches the URL, and -1 is "None", a real value that does.
const URL_SCHEMA = {
	start: urlParam.date(defaultStart),
	end: urlParam.date(defaultEnd),
	productSizeId: urlParam.int(),
	productTypeId: urlParam.int(),
	productionShift: urlParam.oneOf(SHIFT_OPTIONS),
	crewId: urlParam.oneOf(CREW_OPTIONS),
};

export const StraightenerKpisReport = () => {
	const [
		{ start, end, productSizeId, productTypeId, productionShift, crewId },
		setFilters,
		{ resetValues, dropValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<StraightenerKpiRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const dataQuery = useStraightenerKpisData({
		start: startStr,
		end: endStr,
		productSizeId,
		productTypeId,
		crewId,
		productionShift,
	});
	const pickersQuery = useStraightenerKpisPickers();

	const rows = dataQuery.data ?? [];
	const totalRuns = rows.reduce((total, row) => total + (row.Count ?? 0), 0);
	const productTypes = useMemo(
		() =>
			Array.from(
				new Map(
					(pickersQuery.data ?? [])
						.filter((size) => size.ProductTypeId != null)
						.map((size) => [
							size.ProductTypeId!,
							{ Id: size.ProductTypeId!, Name: size.ProductTypeName ?? "" },
						]),
				).values(),
			),
		[pickersQuery.data],
	);
	// Unmatched PLC readings have no MES product type.
	const productSizes = useMemo(
		() => [
			ALL_PRODUCT_SIZE_OPTION,
			...(productTypeId == null ? [NO_PRODUCT_SIZE_OPTION] : []),
			...(pickersQuery.data ?? []).filter(
				(size) => productTypeId == null || size.ProductTypeId === productTypeId,
			),
		],
		[pickersQuery.data, productTypeId],
	);

	const handleGridReady = useCallback(
		(event: GridReadyEvent<StraightenerKpiRow>) => {
			gridApiRef.current = event.api;
		},
		[],
	);

	const handleStartChange = useCallback(
		(d: Moment | null) => {
			if (isUsableDate(d)) setFilters({ start: d });
		},
		[setFilters],
	);

	const handleEndChange = useCallback(
		(d: Moment | null) => {
			if (isUsableDate(d)) setFilters({ end: d });
		},
		[setFilters],
	);

	// Drop stale URL selections, including a product outside the selected type.
	useDropUnknownValue(
		productTypeId,
		pickersQuery.isSuccess,
		useMemo(() => productTypes.map((type) => type.Id), [productTypes]),
		useCallback(() => dropValues("productTypeId"), [dropValues]),
	);
	useDropUnknownValue(
		productSizeId,
		pickersQuery.isSuccess,
		useMemo(() => productSizes.map((size) => size.Id), [productSizes]),
		useCallback(() => dropValues("productSizeId"), [dropValues]),
	);

	// Export the displayed product and layer range, preserving the grid column order.
	const handleExportToExcel = useCallback(() => {
		gridApiRef.current?.exportDataAsExcel({
			fileName: "StraightenerKPIs.xlsx",
			processCellCallback: (params) => {
				const colId = params.column.getColId();
				if (colId === "ProductionDate") return asDate(params.value);
				if (colId === "ProductSize") return productDisplay(params.node?.data);
				if (colId === "TargetBarsPerLayerMin")
					return layerDisplay(params.node?.data);
				if (DECIMAL_COLUMNS.has(colId))
					return params.value == null ? "" : Number(params.value.toFixed(2));
				return params.value ?? "";
			},
		});
	}, []);

	if (dataQuery.error && !errorMsg) {
		setErrorMsg((dataQuery.error as Error).message);
	}
	if (pickersQuery.error && !errorMsg) {
		setErrorMsg((pickersQuery.error as Error).message);
	}

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 130px)",
				gap: 1,
				p: 1,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Straightener KPIs
			</Typography>
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<StraightenerKpisToolbar
					start={start}
					end={end}
					onStartChange={handleStartChange}
					onEndChange={handleEndChange}
					productTypeId={productTypeId}
					productTypes={productTypes}
					onProductTypeChange={(id) =>
						setFilters({ productTypeId: id, productSizeId: null })
					}
					productSizeId={productSizeId}
					onProductSizeChange={(id) =>
						setFilters({
							productSizeId: id === ALL_PRODUCT_SIZE_ID ? null : id,
						})
					}
					productSizes={productSizes}
					productionShift={productionShift}
					onProductionShiftChange={(value) =>
						setFilters({
							productionShift: value as (typeof SHIFT_OPTIONS)[number] | null,
						})
					}
					crewId={crewId}
					onCrewChange={(value) =>
						setFilters({
							crewId: value as (typeof CREW_OPTIONS)[number] | null,
						})
					}
					onReset={resetValues}
					onExportExcel={handleExportToExcel}
					canReset={!isDefault()}
				/>
			</Paper>

			<Paper elevation={1} sx={{ px: 2, py: 0.75 }}>
				<Typography variant="body2">
					<strong>Total:</strong> {totalRuns} runs
				</Typography>
			</Paper>

			<Paper elevation={3} sx={{ flex: 1, overflow: "hidden", minHeight: 0 }}>
				{dataQuery.isInitialLoading ? (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							height: "100%",
						}}
					>
						<CircularProgress />
					</Box>
				) : (
					<Box sx={{ height: "100%" }}>
						<AgGridReact<StraightenerKpiRow>
							columnDefs={COLUMN_DEFS}
							rowData={rows}
							rowModelType="clientSide"
							pagination={false}
							defaultColDef={{
								resizable: true,
								sortable: true,
								cellStyle: CENTER_CELL,
								headerClass: CENTER_HEADER,
							}}
							suppressContextMenu
							enableBrowserTooltips
							// Keep column widths stable when the vertical scrollbar appears.
							alwaysShowVerticalScroll
							suppressColumnVirtualisation
							overlayNoRowsTemplate={NO_ROWS_OVERLAY}
							headerHeight={HEADER_HEIGHT}
							rowHeight={ROW_HEIGHT}
							onGridReady={handleGridReady}
						/>
					</Box>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
